import './RequireSnapshot.css';
import classNames from 'classnames';

import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { makeSelectCurrentSnapshot } from 'pages/SnapshotManagement/selectors';
import type { ImmutableSnapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { type FC, memo } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Link as ReactRouterLink } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { useLocale } from '../LocaleContext';

interface IncompleteLayoutProps {
  stateMessage: string;
  type: 'running' | 'error' | 'canceled';
  children: ReactNode;
}

const NoSnapshotLayout: FC<{ children: ReactNode }> = (props) => (
  <div className='box box-solid mb-0 flex flex-1 flex-col justify-center'>
    <div className='box-body'>
      <div className='require-snapshot__text-wrapper'>{props.children}</div>
    </div>
  </div>
);

const IncompleteLayout: FC<IncompleteLayoutProps> = (props) => (
  <div className='flex flex-col flex-1'>
    <div
      className={classNames(
        'require-snapshot__message',
        {
          running: 'require-snapshot__message--running',
          error: 'require-snapshot__message--error',
          // Canceled shares the running/in-progress bar's yellow styling.
          // It's not an error state, just an incomplete one.
          canceled: 'require-snapshot__message--running',
        }[props.type],
      )}
    >
      {props.type === 'running' && (
        <Icon inline glyph='fa-spinner' loading className='mr-2' size='sm' />
      )}
      {props.stateMessage} Data may be incomplete.{' '}
      <Link
        accent={props.type === 'error' ? 'contrast' : undefined}
        to='/snapshot-management'
      >
        View details
      </Link>
    </div>
    {props.children}
  </div>
);

interface Props {
  children: ReactNode;
}

interface Selector {
  currentSnapshot: ImmutableSnapshot;
}

const selector = createStructuredSelector({
  currentSnapshot: makeSelectCurrentSnapshot(),
});

const BareRequireSnapshot: FC<Props> = (props) => {
  const { currentSnapshot } = useSelector<unknown, Selector>(selector);
  const requireSnapshotMsg = useLocale().app.requireSnapshot;

  if (!currentSnapshot) {
    return (
      <NoSnapshotLayout>
        <h3>{requireSnapshotMsg.selectedSnapshot.title}</h3>
        <p>
          {requireSnapshotMsg.selectedSnapshot.description[0]}{' '}
          <ReactRouterLink to='/snapshot-management'>
            {requireSnapshotMsg.selectedSnapshot.description[1]}
          </ReactRouterLink>{' '}
          {requireSnapshotMsg.selectedSnapshot.description[2]}{' '}
          <ReactRouterLink to='/snapshot-management'>
            {requireSnapshotMsg.selectedSnapshot.description[3]}
          </ReactRouterLink>{' '}
          {requireSnapshotMsg.selectedSnapshot.description[4]}
        </p>
      </NoSnapshotLayout>
    );
  }

  const running = currentSnapshot?.get('running');
  const state = currentSnapshot?.get('state');
  const status = currentSnapshot?.get('status');
  const hasError = status === 'error';
  const isCanceled = status === 'canceled';

  if (running) {
    return (
      <IncompleteLayout
        stateMessage={
          requireSnapshotMsg.discovery[state] ??
          requireSnapshotMsg.discovery.unknownRunningState
        }
        type='running'
      >
        {props.children}
      </IncompleteLayout>
    );
  }

  if (isCanceled) {
    return (
      <IncompleteLayout
        stateMessage={requireSnapshotMsg.discovery.canceled}
        type='canceled'
      >
        {props.children}
      </IncompleteLayout>
    );
  }

  if (hasError) {
    return (
      <IncompleteLayout
        stateMessage={requireSnapshotMsg.discovery.inError}
        type='error'
      >
        {props.children}
      </IncompleteLayout>
    );
  }
  return props.children;
};

export const RequireSnapshot = memo(BareRequireSnapshot);
